import { Frame } from '../components/Frame';
import { Avatar } from '../components/Avatar';
import { CREW, crewMet } from '../data/crew';
import { loadProfile } from '../lib/state';
import { useEffect, useState } from 'react';
import { listFriends, writeUserDoc, type Friend } from '../lib/feed';
import { createInvite } from '../lib/challenges';
import { shareLink } from '../lib/share';
import { useToast } from '../components/Toast';
import { saveProfile } from '../lib/state';
import { track } from '../analytics';
import { getMe, setCrew, downloadExport } from '../lib/crew';
import { useViewed } from '../lib/viewed';

/** Friends come from challenges and invite links (slice 3). Until then: who is here. */
export function Friends() {
  useViewed('friends');
  const [profile, setProfile] = useState(loadProfile);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [name, setName] = useState(profile.name || '');
  const [busy, setBusy] = useState(false);
  const [crew, setCrewState] = useState<'on' | 'off' | 'unasked'>(profile.crew || 'unasked');
  useEffect(() => { getMe().then((m) => setCrewState(m.crew)).catch(() => {}); }, []);
  const toggleCrew = async () => { const next = crew === 'on' ? 'off' : 'on'; try { await setCrew(next); saveProfile({ ...loadProfile(), crew: next }); setCrewState(next); track('crew_toggled', { crew: next }); } catch { show('Could not change that.'); } };
  const { toast, show } = useToast();
  const [editingName, setEditingName] = useState(false);
  const saveName = () => {
    const n = name.trim().slice(0, 40);
    if (n && n !== profile.name) { const p = { ...loadProfile(), name: n }; saveProfile(p); setProfile(p); writeUserDoc({ name: n }).catch(() => {}); }
    setEditingName(false);
  };
  useEffect(() => { listFriends().then(setFriends).catch(() => {}); }, []);
  // The name is an offer, not a gate: an invite with nobody's name on it still
  // works, and asking for one before the link appeared read as a sign-up form
  // (interview 2026-09-18). It is asked once, on Day 2's name tag; the one
  // other place to give or change it is your own row below (owner, 2026-09-30).
  const invite = async () => {
    const n = name.trim().slice(0, 40);
    setBusy(true);
    try {
      if (n && n !== profile.name) { saveProfile({ ...loadProfile(), name: n }); writeUserDoc({ name: n }).catch(() => {}); }
      const inv = await createInvite(n || undefined);
      const outcome = await shareLink(inv.shareUrl, n ? `${n} invited you to make one small thing a day.` : 'Come and make one small thing a day.');
      track('invite_sent', { outcome });
      if (outcome === 'copied') show('Link copied. Send it to them.');
      if (outcome === 'unsupported') { try { await navigator.clipboard.writeText(inv.shareUrl); show('Link copied.'); } catch { show(inv.url); } }
    } catch { show('Could not make the link.'); }
    finally { setBusy(false); }
  };
  return (
    <Frame tabs left={<span className="tag">Friends</span>}>
      <h1>Who is here.</h1>
      <p className="why">{friends.length ? 'Friends see each other’s things set to Friends. Nobody is ranked.' : 'Friends show up here when you send someone a challenge and they finish it. Your crew turn up on your feed one at a time, as you go.'}</p>
      <div className="sheet" style={{ gap: 8 }}>
        <span className="label">Invite a friend</span>
        <p className="why">They get a link. When they join, you see each other’s things set to Friends.</p>
        <button className="btn small coral" style={{ width: '100%' }} onClick={invite} disabled={busy}>{busy ? 'One moment' : 'Send an invite link'}</button>
      </div>
      <div className="stack">
        {/* Your name is its own action, on your own row — not a field under Next on the card (owner, 2026-09-21). */}
        {editingName ? (
          <div className="friend"><Avatar who="me" size={44} name={name || profile.name} /><div className="row" style={{ gap: 8, flex: 1 }}>
            <input className="input" style={{ flex: 1 }} autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveName(); }} placeholder="Your first name" autoComplete="given-name" />
            <button className="btn small" onClick={saveName}>Save</button>
          </div></div>
        ) : (
          <div className="friend"><Avatar who="me" size={44} name={profile.name} /><div className="stack" style={{ gap: 0, flex: 1 }}><b>{profile.name || 'You'}</b><span className="hint">Day {profile.madeDays} of making things · {profile.path}</span></div>
            <button className="link" onClick={() => { setName(profile.name || ''); setEditingName(true); }}>{profile.name ? 'Change name' : 'Add your name'}</button></div>
        )}
        {friends.map((f) => <div className="friend" key={f.uid}><Avatar who="me" size={44} name={f.name || '?'} /><div className="stack" style={{ gap: 0 }}><b>{f.name || 'A friend'}</b><span className="hint">friends since a {f.via}</span></div></div>)}
        {crewMet(profile.met).map((id) => (
          <div className="friend" key={id}><Avatar who={id} size={44} /><div className="stack" style={{ gap: 0 }}><b>{CREW[id].name}</b><span className="hint">{CREW[id].part} · crew</span></div></div>
        ))}
      </div>
      <div className="sheet" style={{ gap: 8 }}>
        <span className="label">How your crew works</span>
        {crew === 'off' && <img className="permission-art" style={{ height: 'min(24dvh, 200px)', objectPosition: '50% 28%' }} src="/art/crew-off.jpg" alt="Your crew, switched off and asleep" />}
        <p className="why">{crew === 'on' ? 'They only work from what you make.' : 'Off: nothing is read, nothing is drawn.'}</p>
        <button className="btn small" style={{ width: '100%', background: crew === 'on' ? 'var(--coral)' : undefined, boxShadow: crew === 'on' ? 'inset 0 -4px 0 var(--coral-dark)' : undefined }} onClick={toggleCrew}>{crew === 'on' ? 'Turn my crew off' : 'Turn my crew on'}</button>
        <button className="link" onClick={() => downloadExport().catch(() => show('Could not export just now.'))}>Download everything you made</button>
      </div>
      {toast}
    </Frame>
  );
}
